import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Stethoscope, 
  HeartPulse, 
  Shield, 
  Award, 
  CheckCircle, 
  Clock, 
  Users,
  Calendar,
  Tag,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Layout from '../components/layout/Layout';
import ScrollReveal from '../components/common/ScrollReveal';
import AnimatedCounter from '../components/common/AnimatedCounter';
import SocietyLogos from '../components/common/SocietyLogos';
import {
  doctorInfo,
  introduction,
  philosophy,
  expertises,
  faq
} from '../data/mock';
import { getPublications } from '../services/api';

const HomePage = () => {
  const [publications, setPublications] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const fetchPublications = async () => {
      try {
        const data = await getPublications();
        setPublications(data.slice(0, 3)); // Only first 3
      } catch (err) {
        console.error('Error fetching publications:', err);
      }
    };
    fetchPublications();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 medical-pattern" />
        <div className="decorative-circle w-96 h-96 -top-48 -right-48 opacity-50" />
        <div className="decorative-circle w-64 h-64 top-1/2 right-1/4 opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full text-teal-700 text-sm font-medium mb-6">
                <CheckCircle className="w-4 h-4" />
                <span>Chirurgien qualifié et expérimenté</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-gray-900 leading-tight">
                {doctorInfo.name}
              </h1>
              <p className="mt-4 text-xl md:text-2xl gradient-text font-medium">
                {doctorInfo.title}
              </p>
              <p className="mt-8 text-lg text-gray-600 leading-relaxed max-w-xl">
                {doctorInfo.tagline}
              </p>
              
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  className="bg-teal-700 hover:bg-teal-800 text-white px-8 py-6 text-base shadow-lg shadow-teal-700/20 hover:shadow-xl hover:shadow-teal-700/30 transition-all"
                >
                  <Link to="/a-propos">
                    Comprendre ma démarche
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 px-8 py-6 text-base"
                >
                  <Link to="/expertises">Quand consulter ?</Link>
                </Button>
              </div>
            </div>

            {/* Hero visual */}
            <div className="hidden lg:block animate-fade-in animate-delay-300">
              <div className="relative">
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center">
                      <Stethoscope className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Excellence chirurgicale</p>
                      <p className="text-sm text-gray-500">Techniques mini-invasives</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Clock className="w-5 h-5 text-teal-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Récupération rapide</p>
                        <p className="text-xs text-gray-500">Chirurgie ambulatoire privilégiée</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Shield className="w-5 h-5 text-teal-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Approche raisonnée</p>
                        <p className="text-xs text-gray-500">Opérer seulement si nécessaire</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Users className="w-5 h-5 text-teal-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Écoute attentive</p>
                        <p className="text-xs text-gray-500">Décision partagée avec le patient</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating badge */}
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                      <Award className="w-5 h-5 text-teal-700" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Certifié</p>
                      <p className="text-xs text-gray-500">Multi-sociétés savantes</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-teal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <ScrollReveal className="text-center">
              <p className="text-4xl md:text-5xl font-light text-white mb-2">
                <AnimatedCounter end={6} suffix="+" />
              </p>
              <p className="text-teal-200 text-sm">Sociétés savantes</p>
            </ScrollReveal>
            <ScrollReveal delay={100} className="text-center">
              <p className="text-4xl md:text-5xl font-light text-white mb-2">
                <AnimatedCounter end={4} />
              </p>
              <p className="text-teal-200 text-sm">Spécialisations</p>
            </ScrollReveal>
            <ScrollReveal delay={200} className="text-center">
              <p className="text-4xl md:text-5xl font-light text-white mb-2">
                <AnimatedCounter end={100} suffix="%" />
              </p>
              <p className="text-teal-200 text-sm">Écoute patient</p>
            </ScrollReveal>
            <ScrollReveal delay={300} className="text-center">
              <p className="text-4xl md:text-5xl font-light text-white mb-2">
                <AnimatedCounter end={24} suffix="h" />
              </p>
              <p className="text-teal-200 text-sm">Suivi post-op</p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-light text-gray-900">
                {introduction.title}
              </h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                {introduction.text}
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 bg-gray-50 medical-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl font-light text-gray-900 text-center mb-4">
              {philosophy.title}
            </h2>
            <p className="text-gray-600 text-center mb-16 max-w-2xl mx-auto">
              Trois piliers fondamentaux guident chaque décision médicale
            </p>
          </ScrollReveal>
          <div className="grid md:grid-cols-3 gap-8">
            {philosophy.points.map((point, index) => {
              const icons = [HeartPulse, Shield, Stethoscope];
              const Icon = icons[index];
              return (
                <ScrollReveal key={index} delay={index * 100}>
                  <Card className="border-0 shadow-sm bg-white hover-lift card-medical h-full">
                    <CardContent className="pt-8 pb-8 px-8">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center mb-6">
                        <Icon className="w-7 h-7 text-teal-700" />
                      </div>
                      <h3 className="text-xl font-medium text-gray-900 mb-4">
                        {point.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {point.description}
                      </p>
                    </CardContent>
                  </Card>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Expertise Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl font-light text-gray-900">
                Domaines d'expertise
              </h2>
              <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
                Chirurgie digestive et pariétale par voie mini-invasive
              </p>
            </div>
          </ScrollReveal>
          <div className="grid md:grid-cols-2 gap-6">
            {expertises.map((expertise, index) => (
              <ScrollReveal key={expertise.id} delay={index * 100}>
                <Link to={`/expertises#${expertise.id}`} className="group block h-full">
                  <Card className="border border-gray-100 shadow-sm hover:shadow-lg hover:border-teal-200 transition-all duration-300 h-full hover-lift">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0 group-hover:bg-teal-100 transition-colors">
                          <span className="text-teal-700 font-semibold">{String(index + 1).padStart(2, '0')}</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-medium text-gray-900 group-hover:text-teal-700 transition-colors">
                            {expertise.title}
                          </h3>
                          <p className="mt-3 text-gray-600 leading-relaxed">
                            {expertise.shortDesc}
                          </p>
                          <span className="inline-flex items-center mt-4 text-sm font-medium text-teal-700 group-hover:gap-2 transition-all">
                            En savoir plus
                            <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Society Logos */}
      <SocietyLogos />

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-3xl font-light text-gray-900">
                Questions fréquentes
              </h2>
              <p className="mt-4 text-gray-600">
                Réponses aux interrogations les plus courantes
              </p>
            </div>
          </ScrollReveal>
          <div className="max-w-3xl mx-auto space-y-4">
            {faq.slice(0, 4).map((item, index) => (
              <ScrollReveal key={index} delay={index * 50}>
                <div 
                  className={`bg-white rounded-xl border transition-all duration-300 ${
                    openFaq === index ? 'border-teal-200 shadow-md' : 'border-gray-100 shadow-sm'
                  }`}
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <span className="font-medium text-gray-900 pr-4">{item.question}</span>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                      openFaq === index ? 'bg-teal-100' : 'bg-gray-100'
                    }`}>
                      {openFaq === index ? (
                        <ChevronUp className="w-4 h-4 text-teal-700" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      )}
                    </div>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${
                    openFaq === index ? 'max-h-48' : 'max-h-0'
                  }`}>
                    <p className="px-6 pb-6 text-gray-600 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal delay={200}>
            <div className="text-center mt-8">
              <Link 
                to="/publications" 
                className="inline-flex items-center text-teal-700 hover:text-teal-800 font-medium"
              >
                Voir toutes les questions
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Recent Articles */}
      {publications.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal>
              <div className="flex items-center justify-between mb-12">
                <div>
                  <h2 className="text-3xl font-light text-gray-900">
                    Articles récents
                  </h2>
                  <p className="mt-2 text-gray-600">
                    Informations médicales et conseils
                  </p>
                </div>
                <Link 
                  to="/publications" 
                  className="hidden md:inline-flex items-center text-teal-700 hover:text-teal-800 font-medium"
                >
                  Tous les articles
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </ScrollReveal>
            <div className="grid md:grid-cols-3 gap-6">
              {publications.map((article, index) => (
                <ScrollReveal key={article.id} delay={index * 100}>
                  <Card className="border border-gray-100 shadow-sm hover:shadow-md hover:border-teal-100 transition-all duration-300 h-full group">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="secondary" className="bg-teal-50 text-teal-700 text-xs">
                          <Tag className="w-3 h-3 mr-1" />
                          {article.category}
                        </Badge>
                      </div>
                      <h3 className="font-medium text-gray-900 mb-2 group-hover:text-teal-700 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(article.created_at)}</span>
                      </div>
                    </CardContent>
                  </Card>
                </ScrollReveal>
              ))}
            </div>
            <div className="text-center mt-8 md:hidden">
              <Link 
                to="/publications" 
                className="inline-flex items-center text-teal-700 hover:text-teal-800 font-medium"
              >
                Tous les articles
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-700 to-teal-800 p-12 lg:p-16">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                  backgroundSize: '32px 32px'
                }} />
              </div>
              
              <div className="relative max-w-2xl mx-auto text-center">
                <h2 className="text-2xl lg:text-3xl font-light text-white mb-4">
                  Une question sur votre situation ?
                </h2>
                <p className="text-teal-100 mb-8 text-lg">
                  La première consultation permet d'évaluer votre situation et de discuter des options adaptées à votre cas.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    asChild
                    className="bg-white text-teal-700 hover:bg-teal-50 px-8 py-6 text-base font-medium shadow-lg"
                  >
                    <Link to="/contact">
                      Prendre rendez-vous
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base"
                  >
                    <a href={`https://wa.me/${doctorInfo.phone.replace(/\\s/g, '').replace('+', '')}`} target="_blank" rel="noopener noreferrer">
                      Contacter sur WhatsApp
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
